import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiGetMenu } from '@/modules/menu/menu-api'
import { useAuthStore } from './auth-store'

/**
 * Permission Configuration
 */
export interface PermissionConfig {
  studio_id: number
  studio_title: string
  user_name: string
  user_id: number
  is_manager: boolean
  can_create_project: boolean
  menus: {
    project_menu?: 0 | 1
    pic_menu?: 0 | 1
    video_menu?: 0 | 1
    resource_menu?: 0 | 1
    audit_menu?: 0 | 1
    media_menu?: 0 | 1
    agent_menu?: 0 | 1
  }
}

/**
 * Permission Store
 * Manages user permissions and menu configuration
 * Only loaded when needed (not on homepage)
 */
export const usePermissionStore = defineStore('permission', () => {
  const authStore = useAuthStore()
  // State
  const config = ref<PermissionConfig | null>(null)
  const isLoading = ref(false)
  const isLoaded = ref(false)

  // Computed
  const isPersonalUser = computed(() => config.value?.studio_id === 0 || false)
  const userName = computed(() => config.value?.user_name || '')
  const studioTitle = computed(() => config.value?.studio_title || '')
  const canCreateProject = computed(() => config.value?.can_create_project || false)
  const isManager = computed(() => config.value?.is_manager || false)

  // Actions
  const setConfig = (newConfig: PermissionConfig) => {
    config.value = newConfig
    isLoaded.value = true
  }

  /**
   * Fetch permissions from server
   * Prevents duplicate requests with isLoading flag
   */
  const fetchPermissions = async () => {
    // Prevent duplicate requests
    if (isLoading.value || isLoaded.value) {
      return config.value
    }

    isLoading.value = true
    try {
      const res = await apiGetMenu({ studio_id: authStore.studioId })

      if (res.data?.menus) {
        const permissionConfig: PermissionConfig = {
          studio_id: res.data.studio_id || 0,
          studio_title: res.data.studio_title || '',
          user_name: res.data.user_name || '',
          user_id: res.data.user_id || 0,
          is_manager: res.data.is_manager || false,
          can_create_project: res.data.can_create_project || false,
          menus: res.data.menus,
        }
        setConfig(permissionConfig)
        authStore.setStudioId(res.data.studio_id || 0)
        return permissionConfig
      }
      return null
    } catch (error) {
      console.error('[PermissionStore] Failed to fetch permissions:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if user has permission for a specific menu
   */
  const hasPermission = (menuKey: keyof PermissionConfig['menus']): boolean => {
    if (!config.value) return false
    return config.value.menus[menuKey] === 1
  }

  /**
   * Clear permissions state
   */
  const clearPermissions = () => {
    config.value = null
    isLoading.value = false
    isLoaded.value = false
  }

  // Migration: Load legacy data from old storage locations
  const migrateLegacyData = () => {
    // Only migrate if current store is empty
    if (config.value) return

    const legacyConfig = localStorage.getItem('userMenuConfig')
    if (legacyConfig) {
      try {
        const parsed = JSON.parse(legacyConfig) as PermissionConfig
        config.value = parsed
        isLoaded.value = true
        console.info('[PermissionStore] Migrated config from legacy userMenuConfig')
        // Clean up old storage
        localStorage.removeItem('userMenuConfig')
      } catch (error) {
        console.error('[PermissionStore] Failed to parse legacy userMenuConfig:', error)
      }
    }
  }

  return {
    // State
    config,
    isLoading,
    isLoaded,
    // Computed
    isPersonalUser,
    userName,
    studioTitle,
    canCreateProject,
    isManager,
    // Actions
    setConfig,
    fetchPermissions,
    hasPermission,
    clearPermissions,
    migrateLegacyData,
  }
}, {
  persist: {
    key: 'permission',
    storage: localStorage,
  },
})
